/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Card, LoadingSection, Skeleton, Typography2, VisuallyHidden } from '@coursera/cds-core';

import _t from 'i18n!nls/enterprise-admin-messaging-v2';

type Props = {
  /** Email subject shown in the metadata block. */
  subject: string;
  /** Ready-to-render email HTML painted inside the sandboxed iframe. */
  html: string;
  /** Sender display name shown next to the From label. */
  fromName: string;
  /** Sender email address shown next to the From label. */
  fromEmail: string;
  /**
   * Recipient address shown next to the To label. Omitted entirely when
   * undefined — a sent message that went to many learners has no single
   * recipient address.
   */
  toAddress?: string;
  /**
   * Drives the whole email block's loading state when the email is fetched
   * asynchronously: the From / To / Subject rows render as skeletons and the
   * iframe area shows a spinner. Defaults to false (e.g. the preview-and-send
   * flow, where everything is known synchronously).
   */
  loading?: boolean;
  error?: boolean;
  /** Namespaces the subject / iframe test ids so each screen stays addressable. */
  dataTestIdPrefix: string;
};

const styles = {
  body: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
  `,
  metadata: css`
    display: grid;
    grid-template-columns: max-content 1fr;
    column-gap: var(--cds-spacing-150);
    row-gap: var(--cds-spacing-50);
  `,
  /* Loading mirror of `metadata`: one skeleton bar per row (label + value as a
     single line) so the block occupies the same vertical space. */
  metadataLoading: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-50);
  `,
  metadataLabel: css`
    color: var(--cds-color-neutral-primary);
  `,
  metadataValue: css`
    color: var(--cds-color-neutral-primary-weak);
    word-break: break-word;
  `,
  /* Grey preview surround (design-owned). The white email card is centred inside
     it; the iframe with the actual email HTML renders within the card. */
  emailFrame: css`
    display: flex;
    justify-content: center;
    padding: var(--cds-spacing-300);
    background: var(--cds-color-neutral-background-primary-weak);
  `,
  /* White email card (a CDS Card: background, border, rounded corners, shadow).
     The iframe renders inside it, so the email HTML only needs to be the content. */
  emailCard: css`
    width: 100%;
    max-width: 600px;
  `,
  iframe: css`
    display: block;
    width: 100%;
    min-height: 480px;
    border: 0;
    background: transparent;
  `,
  /* Loading / error states reserve the iframe's height so the layout doesn't
     jump when the email HTML resolves. */
  frameStatus: css`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 480px;
    padding: var(--cds-spacing-200);
    text-align: center;
  `,
};

/**
 * Email From / To / Subject metadata plus the email rendered inside a sandboxed
 * iframe so the email's inline styles can't leak into the app (and vice versa).
 * Shared by the Preview-and-send and Message-details screens; the caller passes
 * already-compiled `html` and owns the surrounding `Dialog.Content` and any
 * action bar.
 *
 * When the email is fetched asynchronously, pass `loading` to skeleton the
 * metadata rows and show the iframe spinner; pass `error` for the iframe error
 * state.
 */
const EmailPreviewBody = ({
  subject,
  html,
  fromName,
  fromEmail,
  toAddress,
  loading = false,
  error = false,
  dataTestIdPrefix,
}: Props) => {
  // Render "Name <email>" when both are present, otherwise whichever we have.
  const fromValue = fromEmail ? `${fromName} <${fromEmail}>` : fromName;

  // Auto-size the iframe to its content once it loads — mirrors the legacy
  // IframePreview so the email renders at its natural height instead of being
  // clipped to a fixed box. Reading the content document requires the
  // `allow-same-origin` sandbox token set on the iframe below.
  const handleFrameLoad = React.useCallback<React.ReactEventHandler<HTMLIFrameElement>>((event) => {
    const frame = event.currentTarget;
    const contentHeight = frame.contentWindow?.document.body?.scrollHeight;
    if (contentHeight) {
      frame.style.height = `${contentHeight}px`;
    }
  }, []);

  const renderMetadata = () => {
    if (loading) {
      return (
        <div css={styles.metadataLoading}>
          <Typography2 component="p" variant="bodySecondary">
            <Skeleton variant="text" width="35%" data-testid={`${dataTestIdPrefix}-from-skeleton`} />
          </Typography2>
          <Typography2 component="p" variant="bodySecondary">
            <Skeleton variant="text" width="25%" data-testid={`${dataTestIdPrefix}-to-skeleton`} />
          </Typography2>
          <Typography2 component="p" variant="bodySecondary">
            <Skeleton variant="text" width="40%" data-testid={`${dataTestIdPrefix}-subject-skeleton`} />
          </Typography2>
        </div>
      );
    }

    return (
      <div css={styles.metadata}>
        <Typography2 component="span" variant="subtitleSmall" css={styles.metadataLabel}>
          {_t('From')}
        </Typography2>
        <Typography2 component="span" variant="bodySecondary" css={styles.metadataValue}>
          {fromValue}
        </Typography2>
        {toAddress !== undefined ? (
          <React.Fragment>
            <Typography2 component="span" variant="subtitleSmall" css={styles.metadataLabel}>
              {_t('To')}
            </Typography2>
            <Typography2 component="span" variant="bodySecondary" css={styles.metadataValue}>
              {toAddress}
            </Typography2>
          </React.Fragment>
        ) : null}
        <Typography2 component="span" variant="subtitleSmall" css={styles.metadataLabel}>
          {_t('Subject')}
        </Typography2>
        <Typography2
          component="span"
          variant="bodySecondary"
          css={styles.metadataValue}
          data-testid={`${dataTestIdPrefix}-subject`}
        >
          {subject}
        </Typography2>
      </div>
    );
  };

  const renderFrame = () => {
    if (loading) {
      return (
        <div css={styles.frameStatus}>
          <VisuallyHidden>{_t('Loading email preview')}</VisuallyHidden>
          <LoadingSection role="presentation" />
        </div>
      );
    }
    if (error) {
      return (
        <div css={styles.frameStatus} data-testid={`${dataTestIdPrefix}-error`}>
          <Typography2 component="p" variant="bodyPrimary" color="supportText">
            {_t("We couldn't load this email preview. Please try again.")}
          </Typography2>
        </div>
      );
    }
    return (
      <iframe
        title={_t('Email preview')}
        srcDoc={html}
        // `allow-same-origin` lets the email's fonts/assets resolve (and lets us
        // measure its height on load) so it renders like the real email, matching
        // the legacy preview. `allow-scripts` is intentionally omitted — emails
        // carry no JS, and omitting it keeps the sandbox safe alongside same-origin.
        sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        referrerPolicy="no-referrer"
        onLoad={handleFrameLoad}
        css={styles.iframe}
        data-testid={`${dataTestIdPrefix}-iframe`}
      />
    );
  };

  return (
    <div css={styles.body}>
      {renderMetadata()}
      {/* Live region so screen readers announce the loading → email (or error)
          transition when the body is fetched asynchronously. */}
      <div css={styles.emailFrame} aria-live="polite" aria-busy={loading}>
        <Card css={styles.emailCard}>{renderFrame()}</Card>
      </div>
    </div>
  );
};

export default EmailPreviewBody;
